import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import api from '../lib/api';
import { formatDate, STATUS_DISPLAY_NAMES, STATUS_COLORS } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Archive,
  Search,
  Loader2,
  Eye,
  RotateCcw,
  CheckCircle,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const { customersAPI } = (() => {
  return {
    customersAPI: {
      updateStatus: (id, data) => api.put(`/customers/${id}`, data),
    },
  };
})();

export const ArchivePage = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('all');

  const fetchArchived = async () => {
    setLoading(true);
    try {
      const [archivedRes, completedRes] = await Promise.all([
        api.get('/customers', { params: { status: 'archived' } }),
        api.get('/customers', { params: { status: 'completed' } }),
      ]);
      setCustomers([...archivedRes.data, ...completedRes.data]);
    } catch {
      toast.error('Failed to load archived customers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArchived();
  }, []);

  const handleRestore = async (customer) => {
    try {
      await customersAPI.updateStatus(customer.id, { status: 'lead' });
      toast.success(`${customer.first_name} ${customer.last_name} restored to Lead`);
      fetchArchived();
    } catch {
      toast.error('Failed to restore customer');
    }
  };

  const filtered = customers.filter((c) => {
    if (filter === 'archived' && c.status !== 'archived') return false;
    if (filter === 'completed' && c.status !== 'completed') return false;
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      c.first_name?.toLowerCase().includes(q) ||
      c.last_name?.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q) ||
      c.phone?.includes(q) ||
      c.address?.toLowerCase().includes(q)
    );
  });

  const archivedCount = customers.filter((c) => c.status === 'archived').length;
  const completedCount = customers.filter((c) => c.status === 'completed').length;

  return (
    <Layout>
      <div className="space-y-6" data-testid="archive-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-slate-600 flex items-center justify-center">
              <Archive className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Archive</h1>
              <p className="text-slate-500 text-sm">Completed and archived customers</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Badge className="bg-slate-200 text-slate-600 px-3 py-1">
              <Archive className="w-3 h-3 mr-1" />
              {archivedCount} Archived
            </Badge>
            <Badge className="bg-emerald-100 text-emerald-700 px-3 py-1">
              <CheckCircle className="w-3 h-3 mr-1" />
              {completedCount} Completed
            </Badge>
          </div>
        </div>

        <Card>
          <CardContent className="py-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search archived customers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-archive"
                />
              </div>
              <div className="flex gap-2">
                {['all', 'archived', 'completed'].map((f) => (
                  <Button
                    key={f}
                    size="sm"
                    variant={filter === f ? 'default' : 'outline'}
                    onClick={() => setFilter(f)}
                    data-testid={`filter-${f}`}
                    className={filter === f ? 'bg-slate-700 hover:bg-slate-800' : ''}
                  >
                    {f === 'all' ? 'All' : f === 'archived' ? 'Archive/Lost' : 'Completed'}
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12">
                <Archive className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500">No archived customers</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Address</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((c) => (
                    <TableRow key={c.id} data-testid={`archive-row-${c.id}`}>
                      <TableCell>
                        <p className="font-medium text-slate-900">
                          {c.first_name} {c.last_name}
                        </p>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm text-slate-600">{c.email}</p>
                        <p className="text-sm text-slate-500">{c.phone}</p>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm text-slate-600">{c.address}</p>
                        <p className="text-sm text-slate-500">{c.postcode}</p>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${STATUS_COLORS[c.status]} border text-xs`}>
                          {STATUS_DISPLAY_NAMES[c.status]}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm text-slate-500">{formatDate(c.updated_at || c.created_at)}</p>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-2">
                          <Link to={`/customers/${c.id}`}>
                            <Button size="sm" variant="outline" data-testid={`view-${c.id}`}>
                              <Eye className="w-3 h-3 mr-1" />
                              View
                            </Button>
                          </Link>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            onClick={() => handleRestore(c)}
                            data-testid={`restore-${c.id}`}
                          >
                            <RotateCcw className="w-3 h-3 mr-1" />
                            Restore
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};
